/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck - this proxy intentionally straddles ESM/CJS React boundaries.
/* DO NOT add "use client" so SSR works in SSR. */
import type { Dispatch, MutableRefObject, ReactNode, Ref, SetStateAction } from "react";
import * as ReactReal from "react-real";
// @ts-ignore - react-real re-exports from a CommonJS module (`export =`).
export * from "react-real";
// @ts-ignore - react-real re-exports from a CommonJS module (`export =`).
export { default } from "react-real";

type ReactNS = typeof import("react");
const ReactRuntime = ReactReal as unknown as ReactNS;

/* ───────── Build flags ───────── */
const __DEV__ = process.env.NODE_ENV !== "production";
const SAFE_ENABLED = (process.env.NEXT_PUBLIC_SAFE_REACT ?? "0") === "1";
/** 0=off, 1=lean, 2=smart */
const SAFE_LEVEL = (() => {
  const s = process.env.NEXT_PUBLIC_SAFE_REACT_LEVEL;
  if (!SAFE_ENABLED) return 0;
  if (s === "1" || s === "2") return Number(s);
  return __DEV__ ? 2 : 1;
})();

/* ───────── Tiny logger (dead in prod/lean) ───────── */
const LOG_WARN = __DEV__ && SAFE_LEVEL === 2 && (process.env.NEXT_PUBLIC_SAFE_REACT_LOG_LEVEL ?? "warn") !== "silent";
const warn = LOG_WARN ? ((...a: unknown[]) => console.warn("[safe-react]", ...a)) : (() => { });

/* ───────── Fast clocks/microtasks ───────── */
const HAS_PERF = typeof performance !== "undefined" && typeof performance.now === "function";
const now = HAS_PERF ? () => performance.now() : () => Date.now();
const microtask = typeof queueMicrotask === "function" ? queueMicrotask : (fn: () => void) => { Promise.resolve().then(fn); };

/* ───────── Shared types/util ───────── */
type Eq<T> = (a: T, b: T) => boolean;
const isSame: Eq<any> = Object.is;
const useIsoLayout = typeof window !== "undefined" ? ReactRuntime.useLayoutEffect : ReactRuntime.useEffect;
const useLatest = <T,>(v: T) => { const r = ReactRuntime.useRef(v); ReactRuntime.useEffect(() => { r.current = v; }, [v]); return r; };

/* ───────── Config (all constants resolved once) ───────── */
const CFG = {
  // breaker
  limitPerTick: Number(process.env.NEXT_PUBLIC_SAFE_REACT_LIMIT ?? 500),
  minMsBetweenCommits: Number(process.env.NEXT_PUBLIC_SAFE_REACT_MIN_MS ?? 0),
  maxPerFrame: Number(process.env.NEXT_PUBLIC_SAFE_REACT_MAX_PER_FRAME ?? 0), // 0 = off
  // smart layer
  ewmaAlpha: Math.min(Math.max(Number(process.env.NEXT_PUBLIC_SAFE_REACT_EWMA_ALPHA ?? 0.2), 0.01), 0.9),
  anomalyZ: Math.max(Number(process.env.NEXT_PUBLIC_SAFE_REACT_ANOMALY_Z ?? 3), 1),
  warmupSamples: Number(process.env.NEXT_PUBLIC_SAFE_REACT_WARMUP_SAMPLES ?? 8),
  coldMaxRate: Number(process.env.NEXT_PUBLIC_SAFE_REACT_COLD_MAX_RATE ?? 300),
  coldMinGapMs: Number(process.env.NEXT_PUBLIC_SAFE_REACT_COLD_MIN_GAP_MS ?? 2),
  quarantineBaseMs: Number(process.env.NEXT_PUBLIC_SAFE_REACT_QUARANTINE_BASE_MS ?? 16),
  quarantineMaxMs: Number(process.env.NEXT_PUBLIC_SAFE_REACT_QUARANTINE_MAX_MS ?? 256),
  pingPongWindow: Math.max(Number(process.env.NEXT_PUBLIC_SAFE_REACT_PINGPONG_WINDOW ?? 6), 2),
} as const;

/* ───────── Lean breaker (Level 1 & 2 both use it) ───────── */
type BreakerOpts = {
  limitPerTick?: number;
  minMsBetweenCommits?: number;
  maxPerFrame?: number;
  label?: string; // optional tag for debugging
};
const useBreaker = (opts?: BreakerOpts) => {
  if (SAFE_LEVEL === 0) return () => true;

  const limit = opts?.limitPerTick ?? CFG.limitPerTick;
  const minMs = opts?.minMsBetweenCommits ?? CFG.minMsBetweenCommits;
  const maxPerFrame = opts?.maxPerFrame ?? CFG.maxPerFrame;

  const tick = ReactRuntime.useRef(0);
  const scheduled = ReactRuntime.useRef(false);
  const lastCommit = ReactRuntime.useRef(0);
  const frameStart = ReactRuntime.useRef(now());
  const frameCount = ReactRuntime.useRef(0);

  return ReactRuntime.useCallback(() => {
    // microtask budget
    if (++tick.current > limit) return false;
    if (!scheduled.current) {
      scheduled.current = true;
      microtask(() => { tick.current = 0; scheduled.current = false; });
    }

    // frame budget
    if (maxPerFrame > 0) {
      const t = now();
      if (t - frameStart.current > 17) { frameStart.current = t; frameCount.current = 0; }
      if (++frameCount.current > maxPerFrame) return false;
    }

    // min spacing
    if (minMs > 0) {
      const t = now();
      if (t - lastCommit.current < minMs) return false;
      lastCommit.current = t;
    }
    return true;
  }, [limit, minMs, maxPerFrame]);
};

/* ───────── Smart layer (Level 2 only) ───────── */
type EWMA = { mean: number; sqmean: number; ready: boolean };
type Stat = {
  ewma: EWMA;
  lastTs: number;
  samples: number;
  strikes: number;
  quarantineUntil: number;

  // ring buffer for ping-pong (fixed capacity)
  ppBuf: Array<string | undefined>;
  ppHead: number; // next write index
  ppSize: number; // effective size (<= capacity)
};
const STATS: Map<string, Stat> = SAFE_LEVEL === 2 ? new Map() : (undefined);

const ewmaUpdate = (e: EWMA, x: number, a: number): EWMA => {
  const ready = e.ready;
  const mean = ready ? (1 - a) * e.mean + a * x : x;
  const sqmean = ready ? (1 - a) * e.sqmean + a * x * x : x * x;
  return { mean, sqmean, ready: true };
};
const ewmaStats = (e: EWMA) => {
  const v = e.sqmean - e.mean * e.mean;
  return { mean: e.mean, std: v > 0 ? Math.sqrt(v) : 0 };
};

const quarantineMs = (strikes: number) => {
  const s = Math.min(strikes, 16);
  // fast backoff: base * 2^s, clamped
  const ms = CFG.quarantineBaseMs * (1 << s);
  return ms > CFG.quarantineMaxMs ? CFG.quarantineMaxMs : ms;
};

const pingPongHit = (st: Stat) => {
  // pattern A,B,A,B over last 4 entries
  if (st.ppSize < 4) return false;
  const c = st.ppBuf.length;
  const i3 = (st.ppHead + c - 1) % c;
  const i2 = (st.ppHead + c - 2) % c;
  const i1 = (st.ppHead + c - 3) % c;
  const i0 = (st.ppHead + c - 4) % c;
  const a = st.ppBuf[i0], b = st.ppBuf[i1], c2 = st.ppBuf[i2], d = st.ppBuf[i3];
  return a !== b && a === c2 && b === d && a !== undefined && b !== undefined;
};

const recordSetter = (st: Stat, label?: string) => {
  if (!label) return;
  const cap = st.ppBuf.length;
  st.ppBuf[st.ppHead] = label;
  st.ppHead = (st.ppHead + 1) % cap;
  if (st.ppSize < cap) st.ppSize++;
};

const getStat = (id: string): Stat => {
  let s = STATS.get(id);
  if (!s) {
    s = {
      ewma: { mean: 0, sqmean: 0, ready: false },
      lastTs: 0,
      samples: 0,
      strikes: 0,
      quarantineUntil: 0,
      ppBuf: new Array<string | undefined>(CFG.pingPongWindow),
      ppHead: 0,
      ppSize: 0,
    };
    STATS.set(id, s);
  }
  return s;
};

/* ───────── Public hooks ───────── */
export function useState<S>(
  initial: S | (() => S),
  opts?: {
    eq?: Eq<S>;
    limitPerTick?: number;
    minMsBetweenCommits?: number;
    maxPerFrame?: number;
    label?: string; // e.g. "SearchList.query"
    id?: string;    // zone/component id (SmartZone/Profiler id)
  },
): [S, Dispatch<SetStateAction<S>>] {
  if (SAFE_LEVEL === 0) return ReactRuntime.useState(initial);

  const init = typeof initial === "function" ? (initial as () => S) : () => initial;
  const [value, innerSet] = ReactRuntime.useState(init);
  const eqRef = useLatest<Eq<S>>(opts?.eq ?? isSame);
  const valRef = useLatest(value);

  const breaker = useBreaker({
    limitPerTick: opts?.limitPerTick,
    minMsBetweenCommits: opts?.minMsBetweenCommits,
    maxPerFrame: opts?.maxPerFrame,
    label: opts?.label,
  });

  // microtask coalescing (works in both levels)
  const scheduled = ReactRuntime.useRef(false);
  const nextRef = ReactRuntime.useRef<SetStateAction<S> | null>(null);

  const flush = ReactRuntime.useCallback(() => {
    scheduled.current = false;
    const nv = nextRef.current;
    nextRef.current = null;
    if (nv == null) return;

    // Level 2 smart gating (quarantine, warmup, ping-pong)
    if (SAFE_LEVEL === 2 && opts?.id) {
      const st = getStat(opts.id);
      const t = now();
      if (t < st.quarantineUntil) return;

      // quick ping-pong guard (dev-smart only)
      recordSetter(st, opts.label);
      if (pingPongHit(st)) {
        if (__DEV__) warn("ping-pong blocked", { id: opts.id, label: opts.label });
        st.strikes++;
        st.quarantineUntil = t + quarantineMs(st.strikes);
        return;
      }
    }

    if (!breaker()) return;

    if (typeof nv !== "function") {
      if (eqRef.current(valRef.current, nv as S)) return;
      innerSet(nv as S);
      return;
    }
    innerSet((prev) => {
      const resolved = (nv as (p: S) => S)(prev);
      return eqRef.current(prev, resolved) ? prev : resolved;
    });
  }, [breaker, eqRef, innerSet, valRef, opts?.id, opts?.label]);

  const setValue = ReactRuntime.useCallback((next: SetStateAction<S>) => {
    nextRef.current = next;
    if (!scheduled.current) {
      scheduled.current = true;
      microtask(flush);
    }
  }, [flush]);

  if (__DEV__ && SAFE_LEVEL > 0) ReactRuntime.useDebugValue(opts?.label ?? "safe-state");
  return [value, setValue];
}

export function useRef<T>(initialValue: T): MutableRefObject<T>;
export function useRef<T>(initialValue: T | null): MutableRefObject<T | null>;
export function useRef<T>(initialValue: T | null) {
  return ReactRuntime.useRef(initialValue);
}

export function useEvent<F extends (...args: any[]) => any>(fn: F): F {
  const r = ReactRuntime.useRef(fn);
  if (r.current !== fn) r.current = fn;
  useIsoLayout(() => { r.current = fn; }, [fn]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return ReactRuntime.useCallback(((...a: Parameters<F>) => r.current(...a)) as F, []);
}

export function composeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  const list = refs.filter(Boolean);
  return (node: T) => {
    for (const ref of list) {
      if (!ref) continue;
      if (typeof ref === "function") ref(node);
      else (ref as MutableRefObject<T | null>).current = node;
    }
  };
}

export function guardChange<T>(setter: (v: T) => void) {
  let has = false; let last: T;
  return (v: T) => { if (has && Object.is(last, v)) return; has = true; last = v; setter(v); };
}

/* ───────── WatchdogProvider ─────────
   Level 1: global commit fuse only (cheap).
   Level 2: plus per-id EWMA learning, cold-start clamps, adaptive quarantine.
*/
export function WatchdogProvider({
  children,
  id = "safe-react-root",
  commitLimitPerSecond = 200,
}: { children: ReactNode; id?: string; commitLimitPerSecond?: number }) {
  if (SAFE_LEVEL === 0) return ReactRuntime.createElement(ReactRuntime.Fragment, null, children);

  const secondStart = ReactRuntime.useRef(now());
  const commits = ReactRuntime.useRef(0);

  const onRender = ReactRuntime.useCallback((profilerId: string) => {
    const t = now();

    // Global fuse
    if (t - secondStart.current > 1000) { secondStart.current = t; commits.current = 0; }
    if (++commits.current > commitLimitPerSecond) {
      if (SAFE_LEVEL === 2) warn("global commit storm", commits.current);
      // don’t block globally; local breakers/quarantine handle it
    }

    if (SAFE_LEVEL !== 2) return; // lean path ends here

    // Smart path
    const st = getStat(profilerId);
    // honor quarantine on commit source
    if (t < st.quarantineUntil) return;

    // rate since last commit
    const last = st.lastTs || (t - 1);
    const dt = t - last; // ms
    st.lastTs = t;
    const rate = 1000 / (dt <= 0 ? 1 : dt);

    // cold-start absolute clamps
    if (st.samples < CFG.warmupSamples) {
      if (rate > CFG.coldMaxRate || dt < CFG.coldMinGapMs) {
        warn("cold-start clamp", { id: profilerId, rate, dt });
        st.strikes++;
        st.quarantineUntil = t + quarantineMs(st.strikes);
      } else if (st.strikes > 0) {
        st.strikes--; // forgiveness
      }
      st.ewma = ewmaUpdate(st.ewma, rate, CFG.ewmaAlpha);
      st.samples++;
      return;
    }

    // warm: EWMA anomaly
    st.ewma = ewmaUpdate(st.ewma, rate, CFG.ewmaAlpha);
    st.samples++;
    const { mean, std } = ewmaStats(st.ewma);
    if (std > 0) {
      const z = (rate - mean) / std;
      if (Math.abs(z) > CFG.anomalyZ && rate > 50) {
        warn("anomalous component rate", { id: profilerId, rate: +rate.toFixed(1), mean: +mean.toFixed(1), std: +std.toFixed(1), z: +z.toFixed(1) });
        st.strikes++;
        st.quarantineUntil = t + quarantineMs(st.strikes);
      } else if (st.strikes > 0) {
        st.strikes--; // forgiveness
      }
    }
  }, [commitLimitPerSecond]);

  return ReactRuntime.createElement(ReactRuntime.Profiler, { id, onRender }, children);
}

/* Optional helper to tag a subtree; pass the same id in useState opts */
export function SmartZone({ id, children }: { id: string; children: ReactNode }) {
  if (SAFE_LEVEL === 0) return ReactRuntime.createElement(ReactRuntime.Fragment, null, children);
  return ReactRuntime.createElement(ReactRuntime.Profiler, { id, onRender: () => { } }, children);
}
